<script setup lang="ts">
import { useDynamicDialog } from '@/shared/composables/UseDynamicDialog'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from '@/shared/composables/UseToast'
import InternalEmployerSelect from '@/shared/components/form/InternalEmployerSelect.vue'
import SalesGoalPeriodOverviewTable from '@/features/sales/components/SalesGoalPeriodOverviewTable.vue'
import SalesGoalFormDialog from '@/features/sales/dialogs/SalesGoalFormDialog.vue'
import SalesGoalRealizedCompaniesDialog from '@/features/sales/dialogs/SalesGoalRealizedCompaniesDialog.vue'
import {
  useAdminListSalesGoals,
  useAdminGetSalesGoalStats,
  useAdminDeleteSalesGoal,
  getAdminListSalesGoalsQueryKey,
  getAdminListMySalesGoalsQueryKey,
  getAdminGetSalesGoalStatsQueryKey,
} from '@swipe4work/api-orval'
import type {
  AdminListSalesGoalsParams,
  AdminGetSalesGoalStatsParams,
  ListSalesGoalsSalesGoalDto,
  SalesGoalType,
} from '@swipe4work/models-orval'
import { useQueryClient } from '@tanstack/vue-query'
import {
  QUARTER_OPTIONS,
  REVENUE_GOAL_TYPES,
  buildYearOptions,
  formatGoalValue,
  formatPercent,
  formatQuarter,
  formatRevenue,
  goalActionStatus,
  goalPeriodStatus,
  goalPeriodStatusLabel,
  goalPeriodStatusSeverity,
  goalTypeLabel,
  goalTypeSeverity,
  quarterOfDate,
} from '@/features/sales/composables/useSalesGoalAdapter'
import { AdminPermissionResource, PermissionLevel } from '@swipe4work/models-orval'
import { usePermissions } from '@/shared/composables/UsePermissions'

definePage({
  path: '/sales/sales-metrics/goals',
  name: '/sales/SalesMetrics/goals',
  meta: {
    permission: {
      resource: 'SalesMetrics',
      level: 'Read',
    },
  },
})

const dialog = useDynamicDialog()
const confirm = useConfirm()
const toast = useToast()
const queryClient = useQueryClient()
const { can } = usePermissions()

// Two views: revenue goals ("Omzet", New + Existing Business) and churn goals.
type GoalView = 'omzet' | 'churn'
const viewOptions: {
  label: string,
  value: GoalView 
}[] = [
  {
    label: 'Omzet',
    value: 'omzet',
  },
  {
    label: 'Churn',
    value: 'churn',
  },
]
const view = ref<GoalView>('omzet')
const activeTypes = computed<SalesGoalType[]>(() => (view.value === 'omzet' ? REVENUE_GOAL_TYPES : [ 'Churn' ]))
const isChurnView = computed(() => view.value === 'churn')
const statTileCount = computed(() => (isChurnView.value ? 3 : 4))

type GoalFilterState = {
  employerId: number | null
  year: number | null
  quarter: number | null
}

const today = new Date()
const yearOptions = buildYearOptions()

const {
  filters,
  sortField,
  sortDescending,
  offset,
  rows,
  ROWS_PER_PAGE_OPTIONS,
  onSort,
  onPage,
} = useDataTableFilters<GoalFilterState>({
  schema: {
    employerId: 'id',
    year: 'number',
    quarter: 'number',
  },
  // Year + quarter default to the current period.
  defaultFilters: () => ({
    employerId: null,
    year: today.getFullYear(),
    quarter: quarterOfDate(today),
  }),
  persistToStorage: 'sales-goals',
  defaultPageSize: 10,
  defaultSortField: 'periodStart',
  defaultSortDescending: true,
})

// Inline filters apply immediately; go back to the first page on any change.
watch(() => [
  filters.employerId,
  filters.year,
  filters.quarter, 
], () => {
  offset.value = 0
})

const listParams = computed<AdminListSalesGoalsParams>(() => ({
  employerId: filters.employerId ?? undefined,
  types: activeTypes.value,
  year: filters.year ?? undefined,
  quarter: filters.quarter ?? undefined,
  sortField: sortField.value,
  sortDescending: sortDescending.value,
  limit: rows.value,
  offset: offset.value,
}))

const statsParams = computed<AdminGetSalesGoalStatsParams>(() => ({
  employerId: filters.employerId ?? undefined,
  types: activeTypes.value,
  year: filters.year ?? undefined,
  quarter: filters.quarter ?? undefined,
}))

const { data, isPending, isError } = useAdminListSalesGoals(listParams)
const { data: statsData, isPending: isLoadingStats } = useAdminGetSalesGoalStats(statsParams)
const { mutateAsync: deleteGoal } = useAdminDeleteSalesGoal()

const goals = computed<ListSalesGoalsSalesGoalDto[]>(() => data.value?.goals ?? [])
const totalCount = computed(() => data.value?.totalCount ?? 0)
const stats = computed(() => statsData.value)

function openCreateDialog() {
  dialog.open(SalesGoalFormDialog, {
    props: {
      modal: true,
      closable: true,
      showHeader: false,
      dismissableMask: true,
      style: {
        width: 'auto',
        height: 'auto',
      },
    },
  })
}

function openEditDialog(goal: ListSalesGoalsSalesGoalDto) {
  dialog.open(SalesGoalFormDialog, {
    props: {
      modal: true,
      closable: true,
      showHeader: false,
      dismissableMask: true,
      style: {
        width: 'auto',
        height: 'auto',
      },
    },
    data: { goal },
  })
}

function openRealizedCompaniesDialog(goal: ListSalesGoalsSalesGoalDto) {
  dialog.open(SalesGoalRealizedCompaniesDialog, {
    props: {
      modal: true,
      closable: true,
      showHeader: false,
      dismissableMask: true,
      style: {
        width: 'auto',
        height: 'auto',
      },
    },
    data: { goal },
  })
}

function confirmDelete(goal: ListSalesGoalsSalesGoalDto) {
  confirm.require({
    message: `Weet je zeker dat je het doel van ${goal.employerName} (${goalTypeLabel(goal.type)}, ${formatQuarter(goal.year, goal.quarter)}) wilt verwijderen?`,
    header: 'Doel verwijderen',
    icon: 'fa-regular fa-trash',
    acceptLabel: 'Verwijderen',
    acceptClass: 'p-button-danger',
    rejectLabel: 'Annuleren',
    accept: async () => {
      try {
        await deleteGoal({ id: goal.id })
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getAdminListSalesGoalsQueryKey() }),
          queryClient.invalidateQueries({ queryKey: getAdminListMySalesGoalsQueryKey() }),
          queryClient.invalidateQueries({ queryKey: getAdminGetSalesGoalStatsQueryKey() }),
        ])
        toast.success('Doel verwijderd', 'Het doel is succesvol verwijderd')
      }
      catch {
        toast.error('Fout', 'Er is een fout opgetreden bij het verwijderen van het doel')
      }
    },
  })
}

const menu = ref()
const selectedGoal = ref<ListSalesGoalsSalesGoalDto | null>(null)

const menuItems = computed(() => [
  ...(isChurnView.value
    ? []
    : [
      {
        label: 'Realisaties beheren',
        icon: 'fa-regular fa-list',
        command: () => {
          if (selectedGoal.value) openRealizedCompaniesDialog(selectedGoal.value)
        },
      }, 
    ]),
  {
    label: 'Wijzig doel',
    icon: can(AdminPermissionResource.SalesMetrics, PermissionLevel.Write) ? 'fa-regular fa-pen-to-square' : 'fa-regular fa-lock',
    disabled: !can(AdminPermissionResource.SalesMetrics, PermissionLevel.Write),
    command: () => {
      if (selectedGoal.value) openEditDialog(selectedGoal.value)
    },
  },
  {
    label: 'Verwijder doel',
    icon: can(AdminPermissionResource.SalesMetrics, PermissionLevel.Delete) ? 'fa-regular fa-trash' : 'fa-regular fa-lock',
    disabled: !can(AdminPermissionResource.SalesMetrics, PermissionLevel.Delete),
    command: () => {
      if (selectedGoal.value) confirmDelete(selectedGoal.value)
    },
  },
])

function handleClickRow(event: any, goal: ListSalesGoalsSalesGoalDto) {
  selectedGoal.value = goal
  menu.value?.show(event.originalEvent ?? event)
}
</script>

<template>
  <ContextMenu
    ref="menu"
    :model="menuItems"
    @hide="selectedGoal = null"
  />

  <div class="space-y-4">
    <div class="flex flex-wrap items-end justify-between gap-4">
      <div class="flex flex-col gap-2">
        <h3 class="text-lg font-semibold">
          Doelen
        </h3>
        <SelectButton
          v-model="view"
          :options="viewOptions"
          option-label="label"
          option-value="value"
          :allow-empty="false"
          size="small"
        />
      </div>
      <div class="flex flex-wrap items-end gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-xs text-surface-500 font-medium tracking-wide uppercase">Medewerker</label>
          <InternalEmployerSelect v-model="filters.employerId" class="w-56" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-surface-500 font-medium tracking-wide uppercase">Jaar</label>
          <Select
            v-model="filters.year"
            :options="yearOptions"
            placeholder="Alle jaren"
            show-clear
            class="w-32"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-surface-500 font-medium tracking-wide uppercase">Kwartaal</label>
          <Select
            v-model="filters.quarter"
            :options="QUARTER_OPTIONS"
            option-label="label"
            option-value="value"
            placeholder="Alle"
            show-clear
            class="w-32"
          />
        </div>
        <Button
          v-permission="{ resource: AdminPermissionResource.SalesMetrics, level: PermissionLevel.Write }"
          label="Nieuw doel"
          icon="fa-regular fa-plus"
          @click="openCreateDialog"
        />
      </div>
    </div>

    <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <template v-if="isLoadingStats">
        <Skeleton v-for="n in statTileCount" :key="n" height="5rem" />
      </template>
      <template v-else-if="isChurnView">
        <StatTile label="Churndoel" :value="formatPercent(stats?.totalChurnTarget ?? 0)" />
        <StatTile label="Doelen" :value="String(stats?.totalGoals ?? 0)" />
        <StatTile label="Doelen dit kwartaal" :value="String(stats?.currentQuarterGoals ?? 0)" />
      </template>
      <template v-else>
        <StatTile label="Omzetdoel NB" :value="formatRevenue(stats?.totalRevenueNbTarget ?? 0)" />
        <StatTile label="Omzetdoel EB" :value="formatRevenue(stats?.totalRevenueEbTarget ?? 0)" />
        <StatTile label="Medewerkers met doel" :value="String(stats?.personsWithGoals ?? 0)" />
        <StatTile label="Doelen dit kwartaal" :value="String(stats?.currentQuarterGoals ?? 0)" />
      </template>
    </div>

    <Card>
      <template #content>
        <div class="flex flex-col gap-4">
          <h3 class="text-lg font-semibold">
            {{ isChurnView ? 'Churndoelen' : 'Omzetdoelen' }}
          </h3>

          <div v-if="isPending" class="flex flex-col gap-2">
            <Skeleton v-for="n in 5" :key="n" height="3rem" />
          </div>
          <Message v-else-if="isError" severity="error">
            Er is iets misgegaan bij het laden van doelen.
          </Message>
          <DataTable
            v-else
            :value="goals"
            :lazy="true"
            :first="offset"
            :rows="rows"
            :rows-per-page-options="ROWS_PER_PAGE_OPTIONS"
            :total-records="totalCount"
            :sort-field="sortField"
            :sort-order="sortDescending ? -1 : 1"
            paginator
            paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            current-page-report-template="{first}-{last} van {totalRecords}"
            size="small"
            class="w-full"
            row-hover
            @row-click="(e) => handleClickRow(e, e.data)"
            @page="onPage"
            @sort="onSort"
          >
            <template #empty>
              <span class="text-sm text-surface-400">Nog geen doelen gevonden. Maak je eerste doel aan!</span>
            </template>
            <Column field="employerName" header="Medewerker" sortable style="width: 14rem">
              <template #body="{ data: goal }">
                <div class="flex flex-col">
                  <span class="text-sm font-medium">{{ goal.employerName }}</span>
                  <span class="text-xs text-surface-400">{{ goal.employerEmail }}</span>
                </div>
              </template>
            </Column>
            <Column v-if="!isChurnView" field="type" header="Type" sortable style="width: 9rem">
              <template #body="{ data: goal }">
                <Tag :value="goalTypeLabel(goal.type)" :severity="goalTypeSeverity(goal.type)" />
              </template>
            </Column>
            <Column header="Periode" sortable field="periodStart" style="width: 8rem">
              <template #body="{ data: goal }">
                {{ formatQuarter(goal.year, goal.quarter) }}
              </template>
            </Column>
            <Column header="Doelwaarde" sortable field="targetAmount" style="width: 9rem">
              <template #body="{ data: goal }">
                <span class="text-sm font-mono">{{ formatGoalValue(goal.type, goal.targetAmount) }}</span>
              </template>
            </Column>
            <Column header="Status" style="width: 7rem">
              <template #body="{ data: goal }">
                <Tag
                  :value="goalPeriodStatusLabel(goalPeriodStatus(goal.year, goal.quarter))"
                  :severity="goalPeriodStatusSeverity(goalPeriodStatus(goal.year, goal.quarter))"
                />
              </template>
            </Column>
            <Column header="Actie" style="width: 8rem">
              <template #body="{ data: goal }">
                <Tag
                  v-if="goal.isCompleted"
                  value="Afgerond"
                  severity="success"
                  icon="fa-regular fa-check"
                />
                <Tag
                  v-else-if="goalActionStatus(goal) === 'update'"
                  value="Bijwerken"
                  severity="danger"
                  icon="fa-regular fa-pen"
                />
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
    </Card>

    <SalesGoalPeriodOverviewTable :stats="stats" :is-churn-view="isChurnView" />
  </div>
</template>
